import { SelectOffices } from "@cmp-src/form/OfficeSelectorInput/SelectOffices";
import { SelectItem, UnifiedSelect } from "@cmp-src/form/UnifiedSelect";
import { Button } from "@cmp-src/primitives/button";
import { IconButton } from "@cmp-src/primitives/icon-button/IconButton";
import {
  CreateWorkplaceRequestParams,
  WorkplaceRequestTypeElement
} from "@gettactic/api";
import { Alert } from "@gettactic/components/src/data-display/Alert/Alert";
import type { UploadedFileValue } from "@gettactic/components/src/form/FileUploadInput/FileUploadInput.types";
import { HookFormTextInput } from "@gettactic/components/src/form/TextInput";
import { uploadToSignedSlot } from "@gettactic/components/src/upload/uploadToSignedSlot";
import { canScheduleOthers } from "@gettactic/helpers";
import {
  isInformationRequestType,
  isSubmittableRequestType
} from "@gettactic/helpers/src/workplace-requests/requestTypeKind";
import { useAuthenticated } from "@gettactic/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { Paperclip } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CommentAttachmentsTray } from "@/features/settings/components/WorkplaceRequestsPage/components/RequestDetailsModal/CommentAttachmentsTray";
import { InfoBlockCard } from "@/features/workplace-requests/components/InfoBlockCard";
import { usePendingAttachments } from "@/features/workplace-requests/hooks/usePendingAttachments";
import { useWorkplaceRequestMutations } from "@/features/workplace-requests/hooks/useWorkplaceRequestMutations";
import { useWorkplaceRequestTypes } from "@/features/workplace-requests/hooks/useWorkplaceRequestTypes";
import { generateTicketUpload } from "@/features/workplace-requests/lib/ticketUpload";
import {
  CreateWorkplaceRequestFormValues,
  createWorkplaceRequestSchemaFor
} from "@/features/workplace-requests/validation/workplace-requests";
import { Modal } from "@/ui/index";
import { OwnerSelect } from "./OwnerSelect";

/** A photo to attach to the request, e.g. one the user sent to tessa (a data URL) */
export interface CreateRequestPhoto {
  url: string;
  filename: string;
  mediaType: string;
}

export interface CreateRequestDefaultValues {
  title?: string;
  description?: string;
  type?: string;
  location?: string;
  submitter?: string;
  status?: string;
  owner?: string;
}

interface CreateRequestModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isAdmin?: boolean;
  isModal?: boolean;
  defaultValues?: CreateRequestDefaultValues;
  onCreated?: () => void;
  photos?: CreateRequestPhoto[];
  /** Lets the user attach files, e.g. photos tessa no longer has after the conversation was reloaded */
  canAttachFiles?: boolean;
}

type FormValues = CreateWorkplaceRequestFormValues;

export const CreateRequestModal = ({
  isOpen,
  onOpenChange,
  isModal = true,
  defaultValues,
  onCreated,
  photos = [],
  canAttachFiles = false
}: CreateRequestModalProps) => {
  const { createWorkplaceRequest, isLoading } = useWorkplaceRequestMutations();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachedFiles = usePendingAttachments({
    generateUpload: generateTicketUpload
  });

  const { allElements: types } = useWorkplaceRequestTypes();

  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const isAdmin = canScheduleOthers(authenticatedUser);

  // A link type files nothing, so it can never be the type of a request created
  // here. It is filtered out of the picker — but still resolved below, because
  // Tessa hands this modal a type it chose, and a type missing from the list
  // would leave the form pointing at nothing.
  const typeItems = useMemo<SelectItem<WorkplaceRequestTypeElement>[]>(() => {
    return types.filter(isSubmittableRequestType).map((type) => ({
      id: type.id,
      name: type.name,
      keywords: [type.name, type.description || ""].filter(Boolean),
      data: type
    }));
  }, [types]);

  // Whether a type writes its own title decides whether one is required here.
  const resolver = useMemo(
    () =>
      zodResolver(
        createWorkplaceRequestSchemaFor((typeId) =>
          Boolean(types.find((t) => t.id === typeId)?.title_template)
        )
      ),
    [types]
  );

  const form = useForm<FormValues>({
    resolver,
    defaultValues: {
      title: defaultValues?.title || "",
      description: defaultValues?.description || "",
      type: defaultValues?.type || "",
      // Falls back to the filer's own default office so Location doesn't open
      // empty (and required) when nothing more specific was passed in.
      location:
        defaultValues?.location ||
        authenticatedUser.user?.default_office_id ||
        "",
      submitter: defaultValues?.submitter || "",
      status: (defaultValues?.status as never) || "Open",
      owner: defaultValues?.owner || ""
    }
  });

  const selectedType = types.find((t) => t.id === form.watch("type"));
  const assignedUsers = selectedType?.assigned_users || [];

  // Read synchronously, so a second submit during the upload or create doesn't create the request twice
  const submitting = useRef(false);
  // Photos already uploaded by an earlier submit, by URL, so a retry only uploads the ones that failed
  const uploadedPhotos = useRef<Record<string, UploadedFileValue>>({});
  const handleSubmit = async (values: FormValues) => {
    if (submitting.current) return;
    submitting.current = true;
    try {
      await createRequest(values);
    } finally {
      submitting.current = false;
    }
  };

  const createRequest = async (values: FormValues) => {
    const requestData: CreateWorkplaceRequestParams = {
      // Both inputs are hidden for these types, but a value typed before the
      // type was picked would otherwise still be sent.
      title: selectedType?.title_template ? undefined : values.title,
      description: selectedType?.hide_description
        ? undefined
        : values.description,
      type_id: values.type || "",
      office_id: values.location || "",
      status: values.status.toLowerCase() as "open" | "pending" | "closed",
      ...(isAdmin && values.owner && { owner_user_id: values.owner })
    };

    if (photos.length > 0) {
      setIsUploading(true);
      setUploadError(null);
      const results = await Promise.allSettled(
        photos
          .filter((photo) => !uploadedPhotos.current[photo.url])
          .map(async (photo) => {
            const blob = await (await fetch(photo.url)).blob();
            const file = new File([blob], photo.filename, {
              type: photo.mediaType
            });
            uploadedPhotos.current[photo.url] = await uploadToSignedSlot(
              file,
              generateTicketUpload
            );
          })
      );
      setIsUploading(false);
      if (results.some((result) => result.status === "rejected")) {
        setUploadError("The photos couldn't be uploaded. Try again.");
        return;
      }
      requestData.attachments = photos.flatMap((photo) => {
        const uploaded = uploadedPhotos.current[photo.url];
        return uploaded ? [uploaded] : [];
      });
    }
    const attached = attachedFiles.toInput().map((file) => ({
      path: file.path,
      filename: file.filename || "file"
    }));
    if (attached.length > 0) {
      requestData.attachments = [
        ...(requestData.attachments ?? []),
        ...attached
      ];
    }

    try {
      await createWorkplaceRequest(requestData);

      uploadedPhotos.current = {};
      attachedFiles.reset();
      form.reset();
      onOpenChange(false);
      onCreated?.();
    } catch (error) {
      console.error("Failed to create workplace request:", error);
    }
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  // Tessa resolves the type itself and can land on a link type — it is a real
  // answer to "how do I do this", it just isn't a ticket. Show what the admin
  // wrote and offer no way to file, rather than a form that would be rejected.
  const instructionsContent = (
    <div className="flex flex-col gap-4 text-sm">
      <div>
        <h3 className="font-semibold text-base text-neutral-1000">
          {selectedType?.name}
        </h3>
        <p className="mt-0.5 text-neutral-600 text-xs">
          This request is filed outside Tactic — nothing will be tracked here.
        </p>
      </div>
      <InfoBlockCard html={selectedType?.instructions} />
      <Modal.Footer>
        <Button
          className="w-full sm:w-auto"
          variant="outline"
          onClick={handleCancel}
        >
          Close
        </Button>
      </Modal.Footer>
    </div>
  );

  const formContent = (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <div className={"flex max-w-full flex-col gap-6 text-sm"}>
        {/* A type that generates its title builds it from the answers, so
            there is nothing to ask for. */}
        {selectedType?.title_template ? null : (
          <HookFormTextInput
            form={form}
            label={"Title"}
            name={"title"}
            placeholder="Enter request title"
          />
        )}

        {/* Types that hide the description never store one, so don't ask. */}
        {selectedType?.hide_description ? null : (
          <HookFormTextInput
            form={form}
            label={"Description"}
            name={"description"}
            placeholder="Describe the request in detail"
          />
        )}

        {photos.length > 0 && (
          <div className="flex flex-col gap-1">
            <span className="text-secondary-foreground">Photos</span>
            <div className="flex flex-wrap gap-2">
              {photos.map((photo) => (
                <img
                  key={photo.url}
                  src={photo.url}
                  alt={photo.filename}
                  className="size-20 rounded-md object-cover"
                />
              ))}
            </div>
            {uploadError && (
              <Alert
                variant="error"
                canDismiss={false}
                alertTitle={uploadError}
                alertMessage=""
                className="px-4 py-2"
              />
            )}
          </div>
        )}

        {canAttachFiles && (
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <span className="text-secondary-foreground">Attachments</span>
              <IconButton
                icon={Paperclip}
                size="sm"
                variant="ghost"
                onClick={() => fileInputRef.current?.click()}
                disabled={attachedFiles.remainingSlots === 0}
                aria-label="Attach files"
                title={
                  attachedFiles.remainingSlots === 0
                    ? "Attachment limit reached"
                    : "Attach files"
                }
              />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                event.target.value = "";
                if (files.length > 0) attachedFiles.addFiles(files);
              }}
            />
            <CommentAttachmentsTray
              attachments={attachedFiles.attachments}
              onRemove={attachedFiles.remove}
              onRetry={attachedFiles.retry}
            />
          </div>
        )}

        <div className="flex flex-col gap-1">
          <div className="flex w-full items-center justify-between space-x-2 text-secondary-foreground">
            <span>Type</span>
          </div>
          <Controller
            name="type"
            control={form.control}
            rules={{ required: "Type is required" }}
            render={({ field, fieldState }) => {
              const selectedTypeItem =
                typeItems.find((t) => t.id === field.value) || null;
              return (
                <div>
                  <UnifiedSelect
                    items={typeItems}
                    value={selectedTypeItem}
                    onSelect={(item) => field.onChange(item?.id || "")}
                    searchable
                    searchPlaceholder="Search types..."
                    placeholder="Select request type"
                    trigger={
                      <span className="flex h-10 w-full cursor-pointer items-center justify-between gap-1 truncate rounded-md border border-input px-3 text-sm">
                        <span
                          className={
                            selectedTypeItem ? "" : "text-muted-foreground"
                          }
                        >
                          {selectedTypeItem?.name || "Select request type"}
                        </span>
                        <svg
                          className="h-4 w-4 shrink-0 opacity-50"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </span>
                    }
                  />
                  {fieldState.error && (
                    <div className="mt-1">
                      <Alert
                        variant="error"
                        canDismiss={false}
                        alertTitle={fieldState.error.message}
                        alertMessage=""
                        className="px-4 py-2"
                      />
                    </div>
                  )}
                </div>
              );
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex w-full items-center justify-between space-x-2 text-secondary-foreground">
            <span>Location</span>
          </div>
          <Controller
            name="location"
            control={form.control}
            rules={{ required: "Location is required" }}
            render={({ field, fieldState }) => (
              <div>
                <SelectOffices
                  offices={field.value ? [field.value] : []}
                  onChange={(ids) => field.onChange(ids[0] || "")}
                  placeholder="Select Office"
                  triggerClassName="h-10 px-3 border border-input rounded-md w-full justify-between"
                />
                {fieldState.error && (
                  <div className="mt-1">
                    <Alert
                      variant="error"
                      canDismiss={false}
                      alertTitle={fieldState.error.message}
                      alertMessage=""
                      className="px-4 py-2"
                    />
                  </div>
                )}
              </div>
            )}
          />
        </div>

        {/* Owner selection - only show for admins */}
        {isAdmin && (
          <div className="space-y-1">
            <label htmlFor="owner" className="block text-sm">
              Owner
            </label>
            {!selectedType ? (
              <div className="text-neutral-700 text-sm">
                Please select a request type first
              </div>
            ) : assignedUsers.length === 0 ? (
              <div className="text-neutral-700 text-sm">
                No assigned users for this request type
              </div>
            ) : (
              <OwnerSelect
                variant="modal"
                form={form}
                name="owner"
                assignedUsers={assignedUsers}
                placeholder="Select owner (optional)"
              />
            )}
          </div>
        )}
      </div>

      <Modal.Footer>
        <Button
          className="w-full sm:w-auto"
          variant="outline"
          onClick={handleCancel}
          // Creating can't be stopped once it started, so it can't be cancelled either
          disabled={isLoading || isUploading}
        >
          Cancel
        </Button>
        <Button
          className="w-full sm:ml-3 sm:w-auto"
          type="submit"
          disabled={isLoading || isUploading || attachedFiles.isUploading}
          isLoading={isLoading || isUploading}
        >
          Create Request
        </Button>
      </Modal.Footer>
    </form>
  );

  const content =
    selectedType && isInformationRequestType(selectedType)
      ? instructionsContent
      : formContent;

  if (!isModal) return content;

  return (
    <Modal
      title={
        selectedType && isInformationRequestType(selectedType)
          ? "Follow these instructions"
          : "Create New Request"
      }
      isOpen={isOpen}
      onClose={() => onOpenChange(false)}
      size="2xl"
    >
      {content}
    </Modal>
  );
};
